/**
 * os.ts · 09_APP · OS-006
 *
 * The only way this app reaches the database.
 *
 * There is no `from("person").select()` anywhere in this codebase and there
 * must never be one. Every read and every write goes through an `os_` verb,
 * for the same reason the gateway exists: an app given 61 tables will
 * eventually count rows in `mailable` instead of people in `mailable_person`,
 * and send a newsletter three times to 1,172 women and funders.
 *
 * NO SERVER, DELIBERATELY. This is a static app talking straight to PostgREST
 * with the signed-in user's token. Every verb is `security invoker`, so RLS
 * decides what the caller may see, and since v5 the approver on a change is
 * resolved from auth.uid(). A service key would defeat all of that, so there
 * is nowhere in this architecture for one to live. The client being untrusted
 * is the design, not a compromise.
 */

import { supabase } from "./supabase";
import { guardAudience, type AudiencePayload, type AudienceVerdict } from "./audience";
import type { CheckinPerson } from "./checkin_notes";
import type { StaleOpen } from "./stale_close";

export class OsError extends Error {
  readonly hint?: string;
  constructor(message: string, hint?: string) {
    super(message);
    this.name = "OsError";
    this.hint = hint;
  }
}

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    // Postgres refusals are the real safety layer. Pass the message through
    // rather than softening it into something that reads like a hiccup.
    throw new OsError(error.message, error.hint ?? undefined);
  }
  return data as T;
}

/** v58 (2026-10-20): a business card photographed from the Today page. */
export interface CardScan {
  id: string;
  storage_path: string;
  status: string;
  event_label: string | null;
  extracted: Record<string, unknown> | null;
  person_id?: string | null;
  note?: string | null;
  created_at: string;
}

/** v61 (2026-10-20): the People tab (C43). */
export interface PeopleResult {
  db: string; total: number;
  results: Array<{ id: string; name: string; organisation: string | null; job_title: string | null;
    email: string | null; phone: string | null; source: string | null; since: string | null; created_at: string }>;
}
export interface PersonProfile {
  person: { id: string; name: string; first_name: string | null; last_name: string | null; preferred_name: string | null;
    pronouns: string | null; organisation: string | null; job_title: string | null; notes: string | null;
    source: string | null; source_date: string | null; source_detail: string | null; created_at: string;
    needs_contact_detail: boolean } | null;
  emails: Array<{ email: string; primary: boolean; deliverable: boolean }>;
  phones: Array<{ phone: string; primary: boolean; label: string | null }>;
  roles: string[];
  consent: Array<{ channel: string; may_send: boolean }>;
  on_mailing_list: boolean;
  enrollments: Array<{ stage: string; applied_on: string | null; enrolled_on: string | null; completed_on: string | null }>;
  interactions: Array<{ at: string; kind: string; subject: string | null; summary: string | null }>;
  cards: Array<{ storage_path: string; at: string; thank_you: string | null }>;
  /** v62 (2026-10-20): the ten-section community profile (C44, D-0073). */
  last_talked: string | null;
  tasks: Array<{ id: string; title: string; due_on: string | null; status: string }>;
  help: Array<{ id: string; label: string }>;
  organizations: Array<{ name: string; relationship: string; title: string | null; primary: boolean }>;
  connections: Array<{ id: string; name: string; kind: string; mine: boolean; note: string | null }>;
  giving: { count: number; all_time: number; this_year: number; last_on: string | null };
  volunteer_hours: number;
  timeline: Array<{ at: string | null; type: string; kind: string; title: string; detail: string | null; source: string | null }>;
}

/** v62: next steps due today or earlier, for the Today page. */
export interface DueStep { id: string; title: string; due_on: string; person_id: string | null; name: string }

/** v67 (C48): the four impact cards and the women behind each number. Live counts only. */
export interface ImpactRow { id: string | null; name: string | null; how?: string; on?: string; stage?: string }
export interface Chip { label: string; n: number }
export interface Impact {
  year: number; years: number[];
  connected: { count: number; since: string | null; people: ImpactRow[]; by_month: Chip[]; by_heard: Chip[]; by_zip: Chip[] };
  referred: { count: number; referrals: number; since: string | null; by_agency: Chip[];
    rows: Array<{ id: string; person_id: string | null; name: string; agency: string; on: string; today: boolean }> };
  students: { count: number; academies: number; people: ImpactRow[]; by_cohort: Chip[] };
  workshops: { count: number; since: string | null; people: ImpactRow[]; popup: number; online: number; repeat: number };
  /** v70, C50: a headcount in the room, not named, not unique. */
  reached: { count: number; sessions: number; estimated: number; since: string | null; by_place: Chip[];
    rows: Array<{ id: string; on: string; place: string; kind: string; n: number; estimated: boolean; note: string | null; today: boolean }> };
}

/** v71 (C51): one item from her calendar. Title, time and place only, never a description (a Calendly
 *  description can hold a woman's own story). is_outreach marks the ones that earn a "how many women?" ask. */
export interface CalendarItem {
  id: string; title: string; starts_at: string; ends_at: string | null; all_day: boolean;
  location: string | null; is_outreach: boolean;
  /** v72, C54: a networking event, guessed from the title or set by her switch. */
  is_networking?: boolean;
}
/** v72 (C54): a networking event that has ended and she has not answered "did you get any cards?" yet. */
export interface NetworkingDue { id: string; title: string; place: string; starts_at: string; is_outreach: boolean }
/** v71 (C51): an outreach that has ended and has no headcount yet. */
export interface OutreachDue { id: string; title: string; place: string; starts_at: string }

/** v69: a possible duplicate in Waiting on you, both records side by side. */
export interface MatchPair {
  id: string; why: string; confidence: string;
  a: { id: string; name: string; org: string | null; emails: string[] | null; phones: string[] | null; since: string; history: number };
  b: { id: string; name: string; org: string | null; emails: string[] | null; phones: string[] | null; since: string; history: number };
}

/** v63 (C45): what she asked Claude for, from any page. */
export interface AssistantInbox {
  waiting: Array<{ id: string; at: string; said: string; page: string | null; person_id: string | null; person: string | null }>;
  recent_done: Array<{ id: string; said: string; handled_at: string; note: string | null }>;
}

/** v59 (2026-10-20): what her Send tap returns from card-intake. */
export interface CardSendResult {
  person_id: string; is_new: boolean; email: string; first_name: string | null;
  may_thank: boolean; blocked: string | null; sent: boolean; why?: string; error?: string;
}

/** card-intake edge function. Reading never sends; only her tap sends (D-0070). */
async function cardIntake<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("card-intake", { body });
  if (error) {
    let msg = error.message;
    try { const j = await (error as { context?: Response }).context?.json(); if (j?.error) msg = j.error; } catch { /* keep */ }
    throw new OsError(msg);
  }
  return data as T;
}

export interface AttentionPayload {
  pending_proposals: Array<{ id: string; table: string; why: string; since: string }>;
  credentials: Array<{ who: string; what: string; state: string; expires: string | null }>;
  unconfirmed_needs: number;
  review_queue: number;
  stale_referrals: number;
}

/** os_my_day, v48 (2026-10-15). Hours and wellness are two records joined by date. */
export interface MyDay {
  on_date: string;
  sessions: Array<{ id: string; started_at: string; ended_at: string | null; what?: string | null }>;
  hours: number;
  water_glasses: number | null;
  /** v55: the record is ounces; glasses are 8 oz each. */
  water_oz?: number | null;
  mood: string | null;
  felt: string | null;
  /** v56: her feelings journal, many timed entries a day (C39). */
  entries?: Array<{ id: string; at: string; mood: string | null; note: string | null }>;
  /** v57, C40: a stretch still running from an earlier day, if she forgot "Done for today". */
  stale_open?: StaleOpen | null;
}

export interface CheckinList {
  as_of: string;
  quiet_days: number;
  due_total: number;
  no_email: number;
  people: CheckinPerson[];
}

export const os = {
  myDay: () => rpc<MyDay>("os_my_day"),
  workStart: () => rpc<MyDay>("os_work_start"),
  workStop: () => rpc<MyDay>("os_work_stop"),
  /** C40, v57: close yesterday's forgotten stretch at the time she says she stopped. */
  workCloseStale: (id: string, end: Date) => rpc<MyDay>("os_work_close_stale", { p_id: id, p_end: end.toISOString() }),
  /** C36, v54: hours and what she is on, in her words, after she confirms them on screen. */
  workSaid: (action: "start" | "stop" | "stretch" | "what", start: Date | null, end: Date | null, what: string) =>
    rpc<MyDay>("os_work_said", {
      p_action: action,
      p_start: start ? start.toISOString() : null,
      p_end: end ? end.toISOString() : null,
      p_what: what.trim() || null,
    }),
  /** C38, v55: one tap adds a glass (8), a 16 oz or a 32 oz bottle; a negative takes the last one back. */
  addWater: (oz: number) => rpc<MyDay>("os_add_water", { p_oz: oz }),
  /** C39, v56: one journal entry, a mood, her words, or both. */
  feel: (mood: string | null, note: string) => rpc<MyDay>("os_feel", { p_mood: mood, p_note: note.trim() || null }),
  feelRemove: (id: string) => rpc<MyDay>("os_feel_remove", { p_id: id }),
  workEdit: (id: string, what: string) => rpc<MyDay>("os_work_edit", { p_id: id, p_what: what }),
  workRemove: (id: string) => rpc<MyDay>("os_work_remove", { p_id: id }),
  setWellness: (w: { water?: number; mood?: string; felt?: string }) =>
    rpc<MyDay>("os_set_wellness", {
      p_water: w.water ?? null,
      p_mood: w.mood ?? null,
      p_felt: w.felt ?? null,
    }),

  /** Start here card, v51 (2026-10-16). */
  checkinList: (limit = 12) => rpc<CheckinList>("os_checkin_list", { p_limit: limit }),
  checkinSent: (personId: string, draft: string, sent: string) =>
    rpc<{ logged: boolean; learned_from_edit: boolean }>("os_checkin_sent", {
      p_person_id: personId, p_draft: draft, p_sent: sent,
    }),

  /** Scan a card, v58 (2026-10-20). */
  cardScanAdd: (path: string, event?: string) =>
    rpc<{ id: string; status: string }>("os_card_scan_add", { p_path: path, p_event: event ?? null }),
  cardScans: (limit = 3) => rpc<CardScan[]>("os_card_scans", { p_limit: limit }),
  /** v59: read, save and send in one motion (her call, D-0070). Only a card with no email waits. */
  cardRead: () => cardIntake<{ read: number; results: Array<{ id: string; status: string; name?: string | null; email?: string; is_new?: boolean; sent?: boolean; why?: string; error?: string }> }>({ action: "read" }),
  cardSend: (id: string, f: Record<string, string>) => cardIntake<CardSendResult>({ action: "send", id, ...f }),
  peopleSearch: (db: "client" | "community", q: string) =>
    rpc<PeopleResult>("os_people_search", { p_db: db, p_q: q, p_limit: 40 }),
  personProfile: (id: string) => rpc<PersonProfile>("os_person_profile", { p_id: id }),
  /** v62, C44: her own entries on a profile. Each returns the refreshed profile. */
  personLog: (id: string, kind: "note" | "meeting" | "call", text: string) =>
    rpc<PersonProfile>("os_person_log", { p_id: id, p_kind: kind, p_text: text }),
  personTaskAdd: (id: string, title: string, due: string | null) =>
    rpc<PersonProfile>("os_person_task_add", { p_id: id, p_title: title, p_due: due || null }),
  personTaskDone: (taskId: string) => rpc<PersonProfile>("os_person_task_done", { p_task_id: taskId }),
  personGlance: (id: string, text: string) => rpc<PersonProfile>("os_person_glance", { p_id: id, p_text: text }),
  personHelpAdd: (id: string, label: string) => rpc<PersonProfile>("os_person_help_add", { p_id: id, p_label: label }),
  personHelpRemove: (helpId: string) => rpc<PersonProfile>("os_person_help_remove", { p_help_id: helpId }),
  nextStepsDue: () => rpc<DueStep[]>("os_next_steps_due", { p_limit: 10 }),
  /** v63, C45: Ask Claude from anywhere. */
  assistantAsk: (said: string, page: string, personId: string | null) =>
    rpc<AssistantInbox>("os_assistant_ask", { p_said: said, p_page: page, p_person_id: personId }),
  assistantInbox: () => rpc<AssistantInbox>("os_assistant_inbox"),
  /** v67, C48 and C47. */
  impact: (year?: number) => rpc<Impact>("os_impact", { p_year: year ?? null }),
  referralLog: (agency: string, personId: string | null, caller: string, note: string) =>
    rpc<{ id: string; impact: Impact }>("os_referral_log", {
      p_agency: agency, p_person_id: personId, p_caller: caller.trim() || null, p_note: note.trim() || null }),
  referralUndo: (id: string) => rpc<Impact>("os_referral_undo", { p_id: id }),
  /** v70, C50: log the women reached at a talk or workshop. */
  outreachLog: (place: string, headcount: number, on: string | null, kind: string, estimated: boolean, calendarItemId: string | null = null) =>
    rpc<Impact>("os_outreach_log", { p_place: place, p_headcount: headcount, p_on: on || null, p_kind: kind, p_estimated: estimated,
      p_note: null, p_calendar_item_id: calendarItemId }),
  /** v71, C51: her calendar on Today, and the "how many women?" ask after an outreach. */
  comingUp: (days = 7) => rpc<CalendarItem[]>("os_coming_up", { p_days: days }),
  outreachDue: () => rpc<OutreachDue[]>("os_outreach_due"),
  outreachDismiss: (id: string) => rpc<OutreachDue[]>("os_outreach_dismiss", { p_id: id }),
  /** v72, C54: "Did you get any cards?" after a networking event, and her switch on Coming up. */
  networkingDue: () => rpc<NetworkingDue[]>("os_networking_due"),
  networkingAnswer: (id: string, answer: "scanned" | "no_cards" | "not_networking", followUp: string | null = null) =>
    rpc<NetworkingDue[]>("os_networking_answer", { p_id: id, p_answer: answer, p_follow_up: followUp?.trim() || null }),
  calendarMark: (id: string, networking: boolean) => rpc<CalendarItem[]>("os_calendar_mark", { p_id: id, p_networking: networking }),
  outreachUndo: (id: string) => rpc<Impact>("os_outreach_undo", { p_id: id }),
  /** v69: Waiting on you, possible duplicates. */
  matchList: () => rpc<MatchPair[]>("os_match_list"),
  matchDecide: (id: string, same: boolean) => rpc<MatchPair[]>("os_match_decide", { p_id: id, p_same: same }),
  cardDiscard: (id: string) => rpc<{ id: string; status: string }>("os_card_scan_discard", { p_id: id }),

  todayBriefing: () => rpc<Record<string, unknown>>("os_today_briefing"),

  attention: () => rpc<AttentionPayload>("os_attention"),

  personContext: (personId: string) =>
    rpc<Record<string, unknown>>("os_person_context", { p_person_id: personId }),

  searchPeople: (query: string, limit = 10) =>
    rpc<unknown[]>("os_search_people", { p_q: query, p_limit: limit }),

  writableFields: (targetTable?: string) =>
    rpc<unknown[]>("os_writable_fields",
      targetTable ? { p_target_table: targetTable } : {}),

  /**
   * HARD RULE 3. Always returns a verdict, never a bare number, so a caller
   * cannot use the count without having been handed the refusal or the
   * warnings alongside it.
   */
  audience: async (channel: string): Promise<AudienceVerdict> => {
    const payload = await rpc<AudiencePayload>("os_audience", { p_channel: channel });
    return guardAudience(payload);
  },

  /** v79 (D-0100, D-0102): the Email tab on her real mail. */
  mailView: (view: string, box: "emily" | "info") => rpc<MailRow[]>("os_mail_view", { p_view: view, p_box: box, p_limit: 80 }),
  mailCounts: (box: "emily" | "info") => rpc<{ needs: number; handled: number }>("os_mail_counts", { p_box: box }),
  mailThread: (id: string) => rpc<MailThread>("os_mail_thread", { p_thread_id: id }),
  /** Her edit becomes a new draft version, written by her. */
  draftEdit: (draftId: string, subject: string, body: string) =>
    rpc<{ version_id: string; version: number }>("os_draft_edit", { p_draft_id: draftId, p_subject: subject, p_body: body }),
  /** Approve this exact version and queue it. The send step sends within a minute. */
  draftSend: (versionId: string) => rpc<{ send_job_id: string; state: string }>("os_draft_send", { p_draft_version_id: versionId }),
  draftSkip: (threadId: string) => rpc<{ ok: boolean }>("os_draft_skip", { p_thread_id: threadId }),
};

export interface MailRow {
  id: string; subject: string | null; snippet: string | null; at: string | null; from: string | null; last_out: boolean;
  to: string | null; person: string | null; person_id: string | null; category: string | null; messages: number;
  note: string | null; ai: boolean; has_draft: boolean; unread: boolean;
}
export interface MailThread {
  id: string; subject: string | null; person: string | null; person_id: string | null; category: string | null; note: string | null;
  messages: Array<{ id: string; from: string | null; from_addr: string | null; to: string[] | null; at: string; direction: "in" | "out"; body: string; ai: boolean }>;
  draft: null | { draft_id: string; version_id: string; version: number; to: string[]; subject: string; body: string;
    written_by: "claude" | "emily"; status: string; from: string | null };
}
